import { useEffect, useRef } from "react";
import { View, Text } from "react-native";
import { usePostHog } from "posthog-react-native";
import StatusBadge from "./StatusBadge";
import {
  useUserPersona,
  type PersonaHelperRole,
} from "@src/packages/persona/useUserPersona";
import { useFeatureFlag } from "@src/packages/feature-flags/useFeatureFlag";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { DEV_HELPER_ROLE_OVERRIDE } from "./devHelperRoleOverride";
import { cardSurface } from "@louise/moon-ui";

type RoleCopy = { titleKey: string; bodyKey: string };

const ROLE_COPY: Record<NonNullable<PersonaHelperRole>, RoleCopy> = {
  donor: {
    titleKey: "role_banner.donor_title",
    bodyKey: "role_banner.donor_body",
  },
  carrier: {
    titleKey: "role_banner.carrier_title",
    bodyKey: "role_banner.carrier_body",
  },
  "intended-parent": {
    titleKey: "role_banner.intended_parent_title",
    bodyKey: "role_banner.intended_parent_body",
  },
};

export default function RoleAdaptedHomeBanner() {
  const t = useTranslations();
  const { journeyRole, isLoading } = useUserPersona();
  const personaHelperRole: PersonaHelperRole =
    journeyRole === "gestational-carrier" ? "carrier" : journeyRole;
  const flagOn = useFeatureFlag("home_role_adapted_v1");
  const posthog = usePostHog();

  const helperRole = DEV_HELPER_ROLE_OVERRIDE ?? personaHelperRole;
  const isVisible =
    DEV_HELPER_ROLE_OVERRIDE !== null || (flagOn && helperRole !== null);

  // Fire impression once per visible mount so we still get demand analytics
  // by helperRole — replaces the explicit "Notify me" CTA, which had no
  // canonical button style.
  const impressionFiredFor = useRef<PersonaHelperRole>(null);
  useEffect(() => {
    if (
      isLoading ||
      !isVisible ||
      helperRole === null ||
      impressionFiredFor.current === helperRole
    ) {
      return;
    }
    posthog?.capture("helper_role_prompt_viewed", { helper_role: helperRole });
    impressionFiredFor.current = helperRole;
  }, [isLoading, isVisible, helperRole, posthog]);

  if (isLoading || !isVisible || helperRole === null) return null;

  const copy = ROLE_COPY[helperRole];

  return (
    <View
      style={{
        ...cardSurface(),
        marginTop: 4,
        marginBottom: 16,
        padding: 20,
        gap: 10,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 16,
            fontWeight: "600",
            color: EMBER.colors.text,
          }}
        >
          {t(copy.titleKey)}
        </Text>
        <StatusBadge type="coming-soon" />
      </View>

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          color: EMBER.colors.textLight,
          lineHeight: 20,
        }}
      >
        {t(copy.bodyKey)}
      </Text>
    </View>
  );
}
